/* ==========================================================================
   Hidecraft — predictive-search.js
   Combobox search using the Shopify Predictive Search API (section rendering).
   ========================================================================== */

(() => {
  'use strict';

  const { debounce } = window.Hidecraft;

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.dataset.enabled !== 'true') return;
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.live = this.querySelector('[data-predictive-live]');
      this.defaults = this.closest('.search-modal__inner')?.querySelector('[data-search-defaults]');
      this.cache = new Map();

      this.input.addEventListener('input', debounce(() => this.onInput(), 250));
      this.input.addEventListener('focus', () => {
        if (this.input.value.trim() && this.results.innerHTML.trim()) this.open();
      });
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.setSelected(null);
      });
    }

    get query() {
      return this.input.value.trim();
    }

    onInput() {
      const query = this.query;
      if (!query) {
        this.close();
        return;
      }
      this.search(query);
    }

    async search(query) {
      this.controller?.abort();
      this.controller = new AbortController();

      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product,collection,page,article,query',
        'resources[limit]': '4',
        'resources[limit_scope]': 'each',
        section_id: 'predictive-search',
      });
      const url = `${window.theme.routes.predictiveSearch}?${params}`;

      try {
        let html = this.cache.get(url);
        if (!html) {
          this.classList.add('is-searching');
          const res = await fetch(url, { signal: this.controller.signal });
          if (!res.ok) throw new Error(res.status);
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          html = doc.querySelector('#shopify-section-predictive-search')?.innerHTML || '';
          this.cache.set(url, html);
        }
        // Ignore late responses for an older query.
        if (query !== this.query) return;
        this.render(html);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn(error);
          this.close();
        }
      } finally {
        this.classList.remove('is-searching');
      }
    }

    render(html) {
      this.results.innerHTML = html;
      const status = this.results.querySelector('[data-predictive-status]');
      if (this.live && status) {
        this.live.textContent = status.textContent.trim();
        status.remove();
      }
      this.open();
    }

    open() {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      if (this.defaults) this.defaults.hidden = true;
    }

    close() {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      if (this.defaults) this.defaults.hidden = false;
      if (this.live) this.live.textContent = '';
    }

    get options() {
      return Array.from(this.results.querySelectorAll('[role="option"]'));
    }

    setSelected(option) {
      this.options.forEach((el) => el.setAttribute('aria-selected', String(el === option)));
      if (option) {
        this.input.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }

    onKeydown(event) {
      if (this.results.hidden) return;
      const options = this.options;
      if (!options.length) return;
      const current = options.findIndex((el) => el.getAttribute('aria-selected') === 'true');

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.setSelected(options[(current + 1) % options.length]);
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.setSelected(options[current <= 0 ? options.length - 1 : current - 1]);
          break;
        case 'Enter': {
          if (current < 0) return;
          event.preventDefault();
          const target = options[current].querySelector('a, button');
          target?.click();
          break;
        }
        default:
      }
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
})();
